import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Calendar, Users, ArrowRight, Shield, AlertTriangle,
  Loader2, CheckCircle, Plus, ChevronRight, RefreshCw,
} from 'lucide-react';

const STEP_LABELS = ['Current Season', 'Create Season', 'Preview & Promote', 'Confirm & Activate'];

export default function SeasonTransitionPage() {
  const { api } = useAuth();
  const { language } = useLanguage();
  const t = (en, it) => language === 'IT' ? it : en;

  const [step, setStep] = useState(0);
  const [seasons, setSeasons] = useState([]);
  const [activeSeason, setActiveSeason] = useState(null);
  const [loading, setLoading] = useState(true);

  // Create season form
  const [newName, setNewName] = useState('');
  const [newStart, setNewStart] = useState('');
  const [newEnd, setNewEnd] = useState('');
  const [creating, setCreating] = useState(false);

  // Pending season (created but not yet activated)
  const [pendingSeason, setPendingSeason] = useState(null);

  // Preview data
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Promotion overrides: { athlete_id: team_id }
  const [overrides, setOverrides] = useState({});
  const [rtpAction, setRtpAction] = useState('carry');
  const [carryStaff, setCarryStaff] = useState(true);

  // Execution state
  const [promoting, setPromoting] = useState(false);
  const [promoted, setPromoted] = useState(false);
  const [activating, setActivating] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(false);

  const fetchSeasons = useCallback(async () => {
    setLoading(true);
    try {
      const [sRes, aRes] = await Promise.all([
        api.get('/seasons'),
        api.get('/seasons/active'),
      ]);
      setSeasons(sRes.data);
      setActiveSeason(aRes.data);

      // Find pending season
      const pending = sRes.data.find(s => s.status === 'PENDING' || s.status === 'PROMOTING');
      if (pending) {
        setPendingSeason(pending);
        setStep(2);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchSeasons(); }, [fetchSeasons]);

  // Load preview when we have a pending season and reach step 2
  useEffect(() => {
    if (pendingSeason && step >= 2 && !preview) {
      setPreviewLoading(true);
      api.get(`/seasons/${pendingSeason.id}/prepare`)
        .then(r => {
          setPreview(r.data);
          // Initialize overrides with suggested teams
          const ov = {};
          for (const p of r.data.athlete_promotions) {
            ov[p.athlete_id] = p.suggested_team_id;
          }
          setOverrides(ov);
        })
        .catch(err => toast.error('Failed to load preview'))
        .finally(() => setPreviewLoading(false));
    }
  }, [api, pendingSeason, step, preview]);

  const handleCreateSeason = async () => {
    if (!newName || !newStart || !newEnd) {
      toast.error(t('Fill all fields', 'Compila tutti i campi'));
      return;
    }
    setCreating(true);
    try {
      const r = await api.post('/seasons', { name: newName, start_date: newStart, end_date: newEnd });
      toast.success(t(`Season ${r.data.name} created`, `Stagione ${r.data.name} creata`));
      setPendingSeason(r.data);
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setCreating(false);
    }
  };

  const handlePromote = async () => {
    if (!pendingSeason || !preview) return;
    setPromoting(true);
    try {
      const promotions = preview.athlete_promotions.map(p => ({
        athlete_id: p.athlete_id,
        new_team_id: overrides[p.athlete_id] || p.suggested_team_id,
      }));
      const r = await api.post(`/seasons/${pendingSeason.id}/promote`, {
        promotions,
        rtp_action: rtpAction,
      });
      toast.success(t(
        `${r.data.promoted} athletes promoted, ${r.data.new_teams_created} teams created`,
        `${r.data.promoted} atleti promossi, ${r.data.new_teams_created} squadre create`
      ));
      setPromoted(true);
      setStep(3);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Promotion failed');
    } finally {
      setPromoting(false);
    }
  };

  const handleActivate = async () => {
    if (!pendingSeason) return;
    setActivating(true);
    try {
      const r = await api.post(`/seasons/${pendingSeason.id}/activate`, {
        carry_staff: carryStaff,
        rtp_action: rtpAction,
      });
      toast.success(t(
        `Season ${pendingSeason.name} activated!`,
        `Stagione ${pendingSeason.name} attivata!`
      ));
      setConfirmDialog(false);
      await fetchSeasons();
      setStep(0);
      setPendingSeason(null);
      setPreview(null);
      setPromoted(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Activation failed');
    } finally {
      setActivating(false);
    }
  };

  // Group promotions by current team for the UI
  const groupedByTeam = {};
  if (preview) {
    for (const p of preview.athlete_promotions) {
      const key = p.current_team_code;
      if (!groupedByTeam[key]) groupedByTeam[key] = { team: p, athletes: [] };
      groupedByTeam[key].athletes.push(p);
    }
  }

  // Available target teams for override dropdowns
  const targetTeams = preview?.new_teams_preview || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in" data-testid="season-transition-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-zinc-100 flex items-center gap-2">
          <Calendar className="w-6 h-6 text-amber-400" />
          {t('Season Transition', 'Transizione Stagione')}
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          {t('Manage season transitions, team duplication, and athlete promotions',
             'Gestisci transizioni stagionali, duplicazione squadre e promozioni atleti')}
        </p>
      </div>

      {/* Step indicator */}
      <div className="flex items-center gap-2" data-testid="step-indicator">
        {STEP_LABELS.map((label, i) => (
          <React.Fragment key={i}>
            {i > 0 && <ChevronRight className="w-4 h-4 text-zinc-700 shrink-0" />}
            <button
              onClick={() => { if (i <= step) setStep(i); }}
              className={`px-3 py-1.5 rounded-sm text-xs font-medium transition-colors ${
                i === step ? 'bg-amber-500/15 text-amber-400 border border-amber-500/30' :
                i < step ? 'bg-zinc-800 text-zinc-300 border border-zinc-700' :
                'bg-zinc-900 text-zinc-600 border border-zinc-800'
              }`}
              data-testid={`step-${i}`}
            >
              {t(label, label)}
            </button>
          </React.Fragment>
        ))}
      </div>

      {/* STEP 0: Current Season */}
      {step === 0 && (
        <Card className="bg-zinc-900/50 border-zinc-800" data-testid="current-season-card">
          <CardHeader>
            <CardTitle className="text-zinc-100">{t('Current Season', 'Stagione Corrente')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="text-3xl font-bold text-amber-400">{activeSeason?.name || '—'}</div>
              {activeSeason?.is_active && <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/30">Active</Badge>}
              {activeSeason?.inferred && <Badge className="bg-zinc-700 text-zinc-400 border-zinc-600">Inferred from teams</Badge>}
            </div>
            {seasons.length > 0 && (
              <div className="pt-3 border-t border-zinc-800">
                <div className="text-xs text-zinc-500 mb-2">{t('All Seasons', 'Tutte le Stagioni')}</div>
                {seasons.map(s => (
                  <div key={s.id} className="flex items-center gap-3 py-1">
                    <span className="text-sm text-zinc-300">{s.name}</span>
                    <Badge className={`text-[9px] ${s.is_active ? 'bg-emerald-500/15 text-emerald-400' : s.status === 'PENDING' ? 'bg-amber-500/15 text-amber-400' : 'bg-zinc-700 text-zinc-400'}`}>
                      {s.status || 'ACTIVE'}
                    </Badge>
                    <span className="text-[10px] text-zinc-600">{s.start_date} — {s.end_date}</span>
                  </div>
                ))}
              </div>
            )}
            <Button onClick={() => setStep(1)} className="mt-4" data-testid="start-new-season-btn">
              <Plus className="w-4 h-4 mr-2" /> {t('Start New Season', 'Inizia Nuova Stagione')}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* STEP 1: Create New Season */}
      {step === 1 && (
        <Card className="bg-zinc-900/50 border-zinc-800" data-testid="create-season-card">
          <CardHeader>
            <CardTitle className="text-zinc-100">{t('Create New Season', 'Crea Nuova Stagione')}</CardTitle>
            <CardDescription className="text-zinc-500">{t('Define the new season period', 'Definisci il periodo della nuova stagione')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 max-w-md">
            <div>
              <Label className="text-zinc-400">{t('Season Name', 'Nome Stagione')}</Label>
              <Input value={newName} onChange={e => setNewName(e.target.value)} placeholder="2025/2026"
                className="bg-zinc-950 border-zinc-800 mt-1" data-testid="season-name-input" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-zinc-400">{t('Start Date', 'Data Inizio')}</Label>
                <Input type="date" value={newStart} onChange={e => setNewStart(e.target.value)}
                  className="bg-zinc-950 border-zinc-800 mt-1" data-testid="season-start-input" />
              </div>
              <div>
                <Label className="text-zinc-400">{t('End Date', 'Data Fine')}</Label>
                <Input type="date" value={newEnd} onChange={e => setNewEnd(e.target.value)}
                  className="bg-zinc-950 border-zinc-800 mt-1" data-testid="season-end-input" />
              </div>
            </div>
            <Button onClick={handleCreateSeason} disabled={creating} data-testid="create-season-btn">
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              {t('Create Season', 'Crea Stagione')}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* STEP 2: Preview & Promote */}
      {step === 2 && (
        <div className="space-y-4">
          {previewLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />
              <span className="ml-2 text-zinc-500">{t('Loading preview...', 'Caricamento anteprima...')}</span>
            </div>
          ) : preview ? (
            <>
              {/* Summary */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="preview-summary">
                <Card className="bg-zinc-900/50 border-zinc-800">
                  <CardContent className="p-4 text-center">
                    <div className="text-2xl font-bold text-cyan-400">{preview.new_teams_preview.length}</div>
                    <div className="text-xs text-zinc-500">{t('Teams to Create', 'Squadre da Creare')}</div>
                  </CardContent>
                </Card>
                <Card className="bg-zinc-900/50 border-zinc-800">
                  <CardContent className="p-4 text-center">
                    <div className="text-2xl font-bold text-emerald-400">{preview.total_athletes}</div>
                    <div className="text-xs text-zinc-500">{t('Athletes to Promote', 'Atleti da Promuovere')}</div>
                  </CardContent>
                </Card>
                <Card className="bg-zinc-900/50 border-zinc-800">
                  <CardContent className="p-4 text-center">
                    <div className="text-2xl font-bold text-amber-400">{preview.total_staff}</div>
                    <div className="text-xs text-zinc-500">{t('Staff Assignments', 'Assegnazioni Staff')}</div>
                  </CardContent>
                </Card>
                <Card className="bg-zinc-900/50 border-zinc-800">
                  <CardContent className="p-4 text-center">
                    <div className="text-2xl font-bold text-purple-400">{preview.total_rtp}</div>
                    <div className="text-xs text-zinc-500">{t('Active RTP Cases', 'Casi RTP Attivi')}</div>
                  </CardContent>
                </Card>
              </div>

              {/* Team Structure Preview */}
              <Card className="bg-zinc-900/50 border-zinc-800" data-testid="teams-preview-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base text-zinc-100 flex items-center gap-2">
                    <Users className="w-4 h-4 text-cyan-400" />
                    {t('Team Structure', 'Struttura Squadre')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {preview.new_teams_preview.map((tp, i) => (
                      <div key={i} className="flex items-center gap-3 p-2 bg-zinc-950/50 rounded-sm">
                        <Badge className="bg-zinc-800 text-zinc-300 border-zinc-700">{tp.code}</Badge>
                        <span className="text-sm text-zinc-400">{tp.current_season}</span>
                        <ArrowRight className="w-4 h-4 text-amber-400" />
                        <span className="text-sm font-medium text-amber-400">{tp.new_season}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Athlete Promotion Tool */}
              <Card className="bg-zinc-900/50 border-zinc-800" data-testid="promotion-tool-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base text-zinc-100 flex items-center gap-2">
                    <ArrowRight className="w-4 h-4 text-emerald-400" />
                    {t('Athlete Promotions', 'Promozioni Atleti')}
                  </CardTitle>
                  <CardDescription className="text-zinc-500 text-xs">
                    {t('Adjust individual assignments using the dropdown. Bulk promote by leaving defaults.',
                       'Modifica assegnazioni individuali con il menu. Promozione di massa lasciando i default.')}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {Object.entries(groupedByTeam).map(([teamCode, group]) => (
                    <div key={teamCode} className="mb-4" data-testid={`team-group-${teamCode}`}>
                      <div className="flex items-center gap-2 mb-2 pb-1 border-b border-zinc-800/50">
                        <Badge className="bg-cyan-500/15 text-cyan-400 border-cyan-500/30">{teamCode}</Badge>
                        <span className="text-xs text-zinc-500">({group.athletes.length} {t('athletes', 'atleti')})</span>
                        <ArrowRight className="w-3 h-3 text-zinc-600" />
                        <span className="text-xs text-emerald-400">{group.team.suggested_team_code}</span>
                      </div>
                      <div className="space-y-1">
                        {group.athletes.map(a => (
                          <div key={a.athlete_id} className="grid grid-cols-12 gap-2 items-center py-1.5 px-2 bg-zinc-950/30 rounded-sm" data-testid={`promo-row-${a.athlete_id}`}>
                            <div className="col-span-4 text-sm text-zinc-200">{a.athlete_name}</div>
                            <div className="col-span-2 text-xs text-zinc-500">{a.current_team_code}</div>
                            <ArrowRight className="col-span-1 w-3 h-3 text-zinc-600 justify-self-center" />
                            <div className="col-span-5">
                              <Select
                                value={overrides[a.athlete_id] || a.suggested_team_id}
                                onValueChange={v => setOverrides(prev => ({ ...prev, [a.athlete_id]: v }))}
                              >
                                <SelectTrigger className="h-7 text-xs bg-zinc-950 border-zinc-800" data-testid={`promo-select-${a.athlete_id}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-zinc-900 border-zinc-800">
                                  {targetTeams.map(tt => (
                                    <SelectItem key={tt.source_team_id} value={tt.source_team_id}>
                                      {tt.code} ({tt.age_group})
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Staff Assignments */}
              <Card className="bg-zinc-900/50 border-zinc-800" data-testid="staff-preview-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base text-zinc-100 flex items-center gap-2">
                    <Shield className="w-4 h-4 text-amber-400" />
                    {t('Staff Assignments', 'Assegnazioni Staff')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-3 mb-3 p-2 bg-zinc-950/50 rounded-sm border border-zinc-800/50">
                    <button
                      onClick={() => setCarryStaff(!carryStaff)}
                      className={`w-10 h-5 rounded-full transition-colors relative ${carryStaff ? 'bg-emerald-500' : 'bg-zinc-700'}`}
                      data-testid="carry-staff-toggle"
                    >
                      <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${carryStaff ? 'left-5' : 'left-0.5'}`} />
                    </button>
                    <span className="text-sm text-zinc-300">{t('Carry staff assignments to new season', 'Trasferisci assegnazioni staff')}</span>
                  </div>
                  <div className="space-y-1 max-h-48 overflow-y-auto">
                    {preview.staff_assignments.map((s, i) => (
                      <div key={i} className="flex items-center justify-between py-1.5 px-2 text-sm">
                        <span className="text-zinc-300">{s.user_name}</span>
                        <div className="flex items-center gap-2">
                          <Badge className="text-[9px] bg-zinc-800 text-zinc-400 border-zinc-700">{s.role_in_team}</Badge>
                          <span className="text-xs text-zinc-500">{s.team_code}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* RTP Decision */}
              {preview.total_rtp > 0 && (
                <Card className="bg-zinc-900/50 border-zinc-800 border-l-2 border-l-purple-500" data-testid="rtp-decision-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base text-zinc-100 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-purple-400" />
                      {t('Active RTP Cases', 'Casi RTP Attivi')}
                      <Badge className="bg-purple-500/15 text-purple-400 border-purple-500/30 ml-auto">{preview.total_rtp}</Badge>
                    </CardTitle>
                    <CardDescription className="text-zinc-500 text-xs">
                      {t('Choose how to handle active RTP cases during transition',
                         'Scegli come gestire i casi RTP attivi durante la transizione')}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex gap-3">
                      <button
                        onClick={() => setRtpAction('carry')}
                        className={`flex-1 p-3 rounded-sm border text-left transition-colors ${
                          rtpAction === 'carry' ? 'border-emerald-500/50 bg-emerald-500/5' : 'border-zinc-800 bg-zinc-950/50'
                        }`}
                        data-testid="rtp-carry-btn"
                      >
                        <div className={`text-sm font-medium ${rtpAction === 'carry' ? 'text-emerald-400' : 'text-zinc-300'}`}>
                          {t('Carry into New Season', 'Mantieni nella Nuova Stagione')}
                        </div>
                        <div className="text-xs text-zinc-500 mt-1">
                          {t('RTP cases remain active and continue in the new season',
                             'I casi RTP rimangono attivi e continuano')}
                        </div>
                      </button>
                      <button
                        onClick={() => setRtpAction('close')}
                        className={`flex-1 p-3 rounded-sm border text-left transition-colors ${
                          rtpAction === 'close' ? 'border-rose-500/50 bg-rose-500/5' : 'border-zinc-800 bg-zinc-950/50'
                        }`}
                        data-testid="rtp-close-btn"
                      >
                        <div className={`text-sm font-medium ${rtpAction === 'close' ? 'text-rose-400' : 'text-zinc-300'}`}>
                          {t('Close All RTP Cases', 'Chiudi Tutti i Casi RTP')}
                        </div>
                        <div className="text-xs text-zinc-500 mt-1">
                          {t('All active RTP cases will be marked as season-closed',
                             'Tutti i casi RTP attivi verranno chiusi per fine stagione')}
                        </div>
                      </button>
                    </div>
                    <div className="space-y-1">
                      {preview.active_rtp_cases.map((r, i) => (
                        <div key={r.id || i} className="flex items-center justify-between py-1.5 px-2 bg-zinc-950/30 rounded-sm text-sm">
                          <span className="text-zinc-300">{r.athlete_name}</span>
                          <div className="flex items-center gap-2">
                            <Badge className="text-[9px] bg-purple-500/10 text-purple-400 border-purple-500/20">
                              {(r.current_phase || '').replace(/_/g, ' ')}
                            </Badge>
                            <span className="text-[10px] text-zinc-500">{r.injury_type || ''}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Execute button */}
              {!promoted && (
                <Button onClick={handlePromote} disabled={promoting} className="w-full" data-testid="execute-promote-btn">
                  {promoting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ArrowRight className="w-4 h-4 mr-2" />}
                  {t('Execute Promotions & Create Teams', 'Esegui Promozioni e Crea Squadre')}
                </Button>
              )}
              {promoted && (
                <Button onClick={() => setStep(3)} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="proceed-activate-btn">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {t('Proceed to Activation', 'Procedi all\'Attivazione')}
                </Button>
              )}
            </>
          ) : null}
        </div>
      )}

      {/* STEP 3: Confirm & Activate */}
      {step === 3 && (
        <Card className="bg-zinc-900/50 border-zinc-800 border-l-2 border-l-amber-500" data-testid="activation-card">
          <CardHeader>
            <CardTitle className="text-zinc-100 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-amber-400" />
              {t('Confirm Season Activation', 'Conferma Attivazione Stagione')}
            </CardTitle>
            <CardDescription className="text-zinc-500">
              {t('This will activate the new season and archive the current one. Historical data remains fully intact.',
                 'Questo attiverà la nuova stagione e archivierà quella corrente. I dati storici rimangono intatti.')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 bg-zinc-950/50 rounded-sm border border-zinc-800 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-zinc-400">{t('New Season', 'Nuova Stagione')}</span>
                <span className="text-amber-400 font-bold">{pendingSeason?.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-400">{t('Carry Staff', 'Trasferisci Staff')}</span>
                <span className={carryStaff ? 'text-emerald-400' : 'text-zinc-500'}>{carryStaff ? 'Yes' : 'No'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-400">{t('RTP Action', 'Azione RTP')}</span>
                <span className={rtpAction === 'carry' ? 'text-emerald-400' : 'text-rose-400'}>
                  {rtpAction === 'carry' ? t('Carry Over', 'Mantieni') : t('Close All', 'Chiudi Tutti')}
                </span>
              </div>
            </div>

            <div className="p-3 bg-amber-500/5 border border-amber-500/20 rounded-sm text-xs text-amber-400/80">
              {t('This action will create the new season structure and update athlete team assignments. Historical data will remain unchanged.',
                 'Questa azione creerà la struttura della nuova stagione e aggiornerà le assegnazioni. I dati storici rimarranno invariati.')}
            </div>

            <Button
              onClick={() => setConfirmDialog(true)}
              className="w-full bg-amber-600 hover:bg-amber-700"
              data-testid="activate-season-btn"
            >
              {t('Activate New Season', 'Attiva Nuova Stagione')}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog} onOpenChange={setConfirmDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800" data-testid="confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-zinc-100">{t('Are you sure?', 'Sei sicuro?')}</DialogTitle>
            <DialogDescription className="text-zinc-400">
              {t('This will activate the new season. Old teams will be archived but remain accessible for historical data. This action cannot be easily undone.',
                 'Questo attiverà la nuova stagione. Le vecchie squadre verranno archiviate ma resteranno accessibili per i dati storici.')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmDialog(false)} className="border-zinc-700">
              {t('Cancel', 'Annulla')}
            </Button>
            <Button onClick={handleActivate} disabled={activating} className="bg-amber-600 hover:bg-amber-700" data-testid="confirm-activate-btn">
              {activating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              {t('Confirm Activation', 'Conferma Attivazione')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
